<template>
  <div class="kpt-detail-content-page" v-if="device">
    <b-loading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></b-loading>
    <vue-headful :title="`${device.serial} - Features`" />
    <div class="kpt-detail-content-actions">
      <div v-if="isSaving">
        <b-progress></b-progress>
      </div>
      <div class="buttons" v-else>
        <b-button type="is-primary is-outlined" icon-left="save" size="is-small" @click="generateCertificate">Save</b-button>
        <b-button type="is-primary is-outlined" icon-left="download" size="is-small" @click="downloadCertificate" v-if="device.featuresPublicKey">Download Certificate</b-button>
      </div>
      <b-message :active.sync="hasUnsavedChanges" type="is-warning">
        You have unsaved changes, please "Save" before downloading.
      </b-message>
    </div>
    <div class="kpt-device-feature-grid" v-for="feature in enabledFeatureTypes" :key="feature.id">
      <b-field :label="feature.name">
        <b-switch :value="selectedFeatures.includes(feature)" @input="toggleFeature(feature)" :disabled="isSaving">
          {{ selectedFeatures.includes(feature) ? 'Yes' : 'No'}}
        </b-switch>
      </b-field>
      <b-message type="is-light" class="content">
        <p>{{ feature.description}}</p>
      </b-message>
    </div>
  </div>
</template>
<script>
import download from 'downloadjs';

export default {
  data() {
    return {
      isSaving: false,
      isLoading: true,
      hasUnsavedChanges: false,
      selectedFeatures: []
    }
  },
  methods: {
    compareFeatureDifferences() {
      // compare selection and device features to determine differences
      const selectedFeats = this.selectedFeatures.map(sf => sf.shortcode);
      const deviceFeats = this.device.features || [];
      const selectionDiff = selectedFeats.filter(f => !deviceFeats.includes(f));
      const deviceDiff = deviceFeats.filter(f => !selectedFeats.includes(f));

      // toggle unsaved changes message when necessary
      this.hasUnsavedChanges = selectionDiff.length > 0 || deviceDiff.length > 0;
    },
    toggleFeature(feature) {
      // check to see if the feature has already been selected
      const isExisting = this.selectedFeatures.includes(feature);
      if (isExisting) {
        // remove the feature
        const idx = this.selectedFeatures.indexOf(feature);
        this.selectedFeatures.splice(idx, 1);
      } else {
        // add the selection
        this.selectedFeatures.push(feature);
      }

      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'toggleDeviceFeature', 'Sets device feature value', { deviceId: this.device.id });
      this.compareFeatureDifferences();
    },
    async generateCertificate() {
      // prevent spam action spam
      if (this.isSaving) return;

      const features = this.selectedFeatures.map(sf => sf.shortcode);

      // hide warning message if present
      this.hasUnsavedChanges = false;

      // generate the certificate
      this.isSaving = true;
      const result = await this.$store.dispatch('generateDeviceFeaturesCertificate', { 
        device: this.device,
        features
      });
      this.isSaving = false;

      // check for failed request/cert generation
      if (!result) {
        // request failed... check again if we need to show differences message
        this.compareFeatureDifferences();
        this.$buefy.toast.open({
          duration: 2500,
          message: 'Error encountered while attempting to generate certificate.',
          type: 'is-danger'
        });
        return;
      }

      // inform user of success
      this.$buefy.toast.open({
        message: 'Feature Certificate Generated',
        type: 'is-success'
      });
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'generateDeviceCertificate', 'Feature device certificate generated', { deviceId: this.device.id });
    },
    downloadCertificate() {
      // define metadata for .cer file
      const data = this.device.featuresPublicKey;
      const fileName = `${this.device.serial}.cer`;
      const mimeType = 'application/x-x509-ca-cert';

      // initiate download
      download(data, fileName, mimeType);

      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'downloadDeviceCertificate', 'Download device certificate', { deviceId: this.device.id });
    }
  },
  computed: {
    device() {
      return this.$store.state.devices.device;
    },
    featureTypes() {     
      return this.$store.state.devices.features;
    },
    enabledFeatureTypes() {
      return this.featureTypes.filter(ft => ft.enabled);
    }
  },
  async created() {
    // forceful re-acquisition of device configs to support first load (when this page is loaded on refresh or hotlink)
    await this.$store.dispatch('fetchDeviceConfigs');
    
    // populate existing selectedFeatures
    if (this.device.features) {
      this.selectedFeatures = this.enabledFeatureTypes.filter(ft => this.device.features.includes(ft.shortcode));
    }

    // stop loader
    this.isLoading = false;
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      const isAdmin = vm.$hasRole('Super User,Kymeta Admin,Sales Support,Product Support,Account Admin,Credentialed');
      if (!isAdmin) {
        return next('/home');
      }
      return next();
    })
  }
}
</script>
<style lang="scss" scoped>
@import '../../styles/_mixins';

.kpt-detail-content-page {
  position: relative;

  .kpt-device-feature-grid {
    @include display-grid;
    grid-template-columns: 200px 1fr;
    margin-bottom: 15px;
  }
}
</style>
